import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { addNewClient, isAxiosError } from '../../api';

// Components
import Textbox from '../../components/Textbox';
import ConfirmationModal from '../../components/ConfirmationModal';
import Select, { SelectOption, SingleSelection } from '../../components/Select';

// Types
import { NewClientData, POSTErrorRes } from '../../types';

// Utils
import { getClientNameError } from '../../utils';

interface NewClientModalProps {
  agencyValue: SingleSelection;
  agencyOptions: SelectOption[];
  onChangeAgency: (value: SingleSelection) => void;
  onCancel: () => void;
  onSubmit: (newClient: NewClientData | null) => void | Promise<void>;
}

const FORM_ID = 'add-client-form';

function NewClientModal({
  agencyValue,
  agencyOptions,
  onChangeAgency,
  onCancel,
  onSubmit,
}: NewClientModalProps) {
  const [clientName, setClientName] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { mutate, isLoading } = useMutation({
    mutationFn: () => {
      if (!agencyValue?.value) {
        throw new Error('Agency is required');
      }
      if (!clientName) {
        throw new Error('Client name is required.');
      }
      return addNewClient(parseInt(agencyValue.value), clientName);
    },
    onSuccess: (res) => {
      toast.success(res.message);
      onSubmit(res.data);
    },
    onError: (res) => {
      let message = 'An unknown error occurred.';
      if (isAxiosError<POSTErrorRes>(res)) {
        message = res.response?.data.error.message || message;
      }
      toast.error(message);
    },
  });

  const onChangeClientName = (value: string) => {
    setErrorMsg(getClientNameError(value));
    setClientName(value);
  };

  const onSubmitForm = (evt: React.FormEvent) => {
    evt.preventDefault();
    if (errorMsg) return;
    mutate();
  };

  // JSX
  return (
    <ConfirmationModal
      isOpen={true}
      header="Add Client"
      isLoading={isLoading}
      formID={FORM_ID}
      content={
        <form
          id={FORM_ID}
          className="w-96 flex flex-col gap-6"
          onSubmit={onSubmitForm}
        >
          <Select
            value={agencyValue}
            label="Agency"
            options={agencyOptions}
            onChange={onChangeAgency}
            required
          />
          <Textbox
            label="Client Name"
            value={clientName}
            onChange={onChangeClientName}
            errorMessage={errorMsg}
            required
          />
        </form>
      }
      onCancel={onCancel}
      isDisabled={!!errorMsg}
      isForm
    />
  );
}

export default NewClientModal;
